import React from 'react';
import { ChevronDown, ChevronUp, Calculator, Code, TrendingUp } from 'lucide-react';

interface TheoreticalMaximumStepProps {
  expandedSection: string | null;
  toggleSection: (section: string) => void;
}

export const TheoreticalMaximumStep: React.FC<TheoreticalMaximumStepProps> = ({
  expandedSection,
  toggleSection,
}) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 cursor-pointer" onClick={() => toggleSection('theoretical')}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-gray-100 text-gray-700 flex items-center justify-center text-sm font-semibold">2</div>
            <h3 className="text-base font-semibold text-black">Theoretical Maximum Calculation</h3>
          </div>
          {expandedSection === 'theoretical' ? <ChevronUp className="w-4 h-4 text-gray-600" /> : <ChevronDown className="w-4 h-4 text-gray-600" />}
        </div>
      </div>
      {expandedSection === 'theoretical' && (
        <div className="p-6">
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              The theoretical maximum represents perfect load shifting with unlimited flexibility but respecting
              grid capacity constraints. This serves as our benchmark.
            </p>

            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-xs font-semibold text-purple-700 mb-2">
                <Calculator className="w-3 h-3 inline mr-1" />
                MATHEMATICAL FORMULATION
              </p>
              <div className="bg-white p-3 rounded border border-purple-200">
                <p className="text-xs font-mono mb-2">Maximize: Σ(consumption_h × (retail_price - spot_price_h))</p>
                <p className="text-xs font-mono mb-2">Subject to:</p>
                <ul className="text-xs font-mono space-y-1 ml-4">
                  <li>• consumption_h ≤ max_hourly_consumption ∀h</li>
                  <li>• Σ(consumption_h) = total_annual_consumption</li>
                  <li>• consumption_h ≥ 0 ∀h</li>
                </ul>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-xs font-semibold text-gray-700 mb-2">
                <Code className="w-3 h-3 inline mr-1" />
                IMPLEMENTATION
              </p>
              <pre className="text-xs bg-white p-3 rounded border border-gray-300 overflow-x-auto">
{`def calculate_theoretical_maximum(consumption, prices, retail_tariff):
    # Find peak hourly consumption (grid limit)
    max_hourly = max(hourly_consumption)  # 2,339 kWh for Garibaldi
    total_consumption = sum(consumption)   # 10,466,382 kWh

    # Calculate value for each hour
    opportunities = []
    for hour, price in enumerate(prices):
        value_per_mwh = max(0, retail_tariff - price)
        opportunities.append((hour, value_per_mwh))

    # Sort by value (best hours first)
    opportunities.sort(key=lambda x: x[1], reverse=True)

    # Fill best hours with consumption
    remaining = total_consumption
    theoretical_savings = 0

    for hour, value_per_mwh in opportunities:
        consumption_this_hour = min(max_hourly, remaining)
        savings = (consumption_this_hour / 1000) * value_per_mwh
        theoretical_savings += savings
        remaining -= consumption_this_hour
        if remaining <= 0:
            break

    return theoretical_savings  # Varies by data period`}</pre>
            </div>

            <div className="bg-green-50 p-3 rounded-lg">
              <p className="text-xs font-semibold text-green-800 mb-1">
                <TrendingUp className="w-3 h-3 inline mr-1" />
                Real Results (Garibaldi Data)
              </p>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <p className="text-green-600">Current Cost</p>
                  <p className="font-bold">$206,680*</p>
                </div>
                <div>
                  <p className="text-green-600">Theoretical Cost</p>
                  <p className="font-bold">~$0</p>
                </div>
                <div>
                  <p className="text-green-600">Max Savings</p>
                  <p className="font-bold text-green-700">~$206,000</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};